import React, { useState, useEffect } from "react";
import { T } from "../lib/theme";
import { US_STATES } from "../lib/usStates";
import { Spin } from "./Atoms";

// Self-service profile editing — full_name/phone/state/zip, the same four
// fields admin/update-user.js already lets an admin edit on someone else's
// behalf, now editable by the account holder directly via the extended
// PATCH /api/me (functions/api/me.js). No props, no parent state — fetches
// its own data on mount, same self-contained shape as AdminDashboard.jsx.
//
// Deliberately no hard-required gating on any field, matching PATCH
// /api/me's own contract: clearing your name to blank and saving is
// allowed here, unlike the admin edit path, since this is a self-service
// form, not account creation. Email is shown but not editable — same
// anti-enumeration reasoning that keeps it out of every other edit path
// in this app, not a gap in this form specifically.
export function AccountSettings(){
  const[loading,setLoading]=useState(true);
  const[loadError,setLoadError]=useState("");
  const[email,setEmail]=useState("");
  const[fullName,setFullName]=useState("");
  const[phone,setPhone]=useState("");
  const[state,setState]=useState("");
  const[zip,setZip]=useState("");
  const[saving,setSaving]=useState(false);
  const[saveError,setSaveError]=useState("");
  const[saved,setSaved]=useState(false);

  useEffect(()=>{
    let cancelled=false;
    fetch("/api/me").then(r=>r.json()).then(d=>{
      if(cancelled)return;
      setEmail(d.email||"");
      setFullName(d.fullName||"");
      setPhone(d.phone||"");
      setState(d.state||"");
      setZip(d.zip||"");
      setLoading(false);
    }).catch(()=>{
      if(!cancelled){setLoadError("Could not load your account details.");setLoading(false);}
    });
    return()=>{cancelled=true;};
  },[]);

  const save=async()=>{
    setSaving(true);setSaveError("");setSaved(false);
    try{
      const res=await fetch("/api/me",{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({full_name:fullName,phone,state,zip})});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save.");
      setSaved(true);
      setTimeout(()=>setSaved(false),2000);
    }catch(e){
      setSaveError(e.message||"Could not save.");
    }
    setSaving(false);
  };

  const inp={width:"100%",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"9px 12px",fontSize:14,color:T.text,background:T.cardAlt,outline:"none",boxSizing:"border-box"};
  const lbl={fontSize:12,fontWeight:600,color:T.textMd,display:"block",marginBottom:5};
  const hint={fontSize:10.5,color:T.textSm,marginTop:4,lineHeight:1.4};

  if(loading)return(
    <div style={{maxWidth:520,margin:"40px auto",padding:"0 16px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,color:T.textMd,fontSize:14}}><Spin sz={18}/>Loading your account...</div>
    </div>
  );

  return(
    <div style={{maxWidth:520,margin:"0 auto",padding:"20px 16px 52px"}}>
      <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:16}}>Account Settings</div>
      <div style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"20px 22px"}}>
        {loadError&&<div style={{fontSize:13,color:T.red,marginBottom:14}}>{loadError}</div>}

        <div style={{marginBottom:16}}>
          <label style={lbl}>Email</label>
          <div style={{fontSize:14,color:T.textMd,padding:"9px 0"}}>{email}</div>
          <div style={hint}>Email can't be changed here — contact the account owner if it needs to be updated.</div>
        </div>

        <div style={{marginBottom:16}}>
          <label style={lbl}>Full Name</label>
          <input style={inp} value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="Your name"/>
        </div>

        <div style={{marginBottom:16}}>
          <label style={lbl}>Phone</label>
          <input style={inp} value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Phone (optional)"/>
        </div>

        <div style={{marginBottom:16}}>
          <label style={lbl}>State</label>
          <select style={{...inp,color:state?T.text:T.textSm}} value={state} onChange={e=>setState(e.target.value)}>
            <option value="">State (optional)</option>
            {US_STATES.map(s=><option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
          <div style={hint}>Used for regional gas-price estimates.</div>
        </div>

        <div style={{marginBottom:20}}>
          <label style={lbl}>ZIP Code</label>
          <input style={inp} inputMode="numeric" value={zip} onChange={e=>setZip(e.target.value.replace(/[^0-9]/g,"").slice(0,5))} placeholder="ZIP (optional)"/>
          <div style={hint}>Used to find dealers and listings near you.</div>
        </div>

        {saveError&&<div style={{fontSize:13,color:T.red,marginBottom:14}}>{saveError}</div>}
        <button onClick={save} disabled={saving} style={{width:"100%",background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:9,padding:"11px 0",fontSize:14,fontWeight:700,opacity:saving?.6:1}}>
          {saving?"Saving...":saved?"Saved ✓":"Save Changes"}
        </button>
      </div>
    </div>
  );
}
